import { createColumnHelper } from '@tanstack/react-table';
import { X, CircleAlert, RotateCw, Check } from 'lucide-react';

import { getPlatformType } from '@/react/portainer/environments/utils';
import {
  AppliedPolicyStatusType,
  PlatformType,
} from '@/react/portainer/environments/types';
import {
  EnvironmentInfo,
  PolicyEnvironmentType,
  PolicyType,
  isEdgeAsync,
} from '@/react/portainer/policies/types';

import { Badge } from '@@/Badge';
import { Link } from '@@/Link';
import { Button } from '@@/buttons';

import { getEnvironmentPolicyStatus } from '../../utils/isEnvironmentTypeSupportedByPolicy';

import { getStatusDisplayLabel } from './statusUtils';

const envColumnHelper = createColumnHelper<EnvironmentInfo>();

export function getEnvironmentColumns(
  policyType?: PolicyType,
  policyEnvType?: PolicyEnvironmentType
) {
  return [
    envColumnHelper.accessor('Name', {
      header: 'Environment name',
      id: 'name',
      meta: { width: '250px' },
      enableSorting: true,
      cell: ({ getValue, row: { original: env } }) => {
        const { to: linkTo, params: linkParams } = getEnvironmentLink(env);
        return (
          <Link
            to={linkTo}
            params={linkParams}
            data-cy={`environment-link-${env.Id}`}
            className="truncate"
            title={getValue()}
          >
            {getValue()}
          </Link>
        );
      },
    }),
    envColumnHelper.accessor((env) => getStatusLabel(env, policyType), {
      header: 'Status',
      id: 'status',
      meta: { width: '150px' },
      enableSorting: false,
      cell: ({ getValue }) => {
        const statusLabel = getValue();
        switch (statusLabel) {
          case 'Warning':
          case 'No policy type set':
            return (
              <Badge type="warn">
                <CircleAlert className="h-3 w-3 mr-1" />
                {statusLabel}
              </Badge>
            );
          case 'Failed':
            return (
              <Badge type="danger">
                <X className="h-3 w-3 mr-1" />
                {statusLabel}
              </Badge>
            );
          case 'Not Supported':
            return <Badge type="muted">{statusLabel}</Badge>;
          case 'In Progress':
            return <Badge type="info">{statusLabel}</Badge>;
          case 'Active':
            return (
              <Badge type="success">
                <Check className="h-3 w-3 mr-1" />
                Active
              </Badge>
            );
          default:
            return <Badge type="muted">{statusLabel}</Badge>;
        }
      },
    }),
    envColumnHelper.accessor(
      (row) => {
        if (!policyType) {
          return "Policy doesn't have an type set";
        }
        if (!policyEnvType) {
          return "Policy doesn't have an environment type set";
        }

        const status = getEnvironmentPolicyStatus(row, policyType);

        if (status.Status === AppliedPolicyStatusType.InProgress) {
          return 'The policy is currently being applied to this environment';
        }

        return status.Message || '-';
      },
      {
        header: 'Message',
        id: 'message',
        meta: { width: '350px' },
        enableSorting: false,
        cell: ({ getValue }) => <span>{getValue()}</span>,
      }
    ),
    envColumnHelper.accessor(() => 'Retry', {
      header: 'Action',
      id: 'action',
      meta: { width: '120px' },
      enableSorting: false,
      cell: ({ row }) => (
        <Button
          icon={RotateCw}
          title="Retry policy distribution"
          disabled
          color="default"
          data-cy={`retry-policy-button-${row.original.Id}`}
        >
          Retry
        </Button>
      ),
    }),
  ];
}

export function getStatusLabel(env: EnvironmentInfo, policyType?: PolicyType) {
  if (!policyType) {
    return 'No policy type set';
  }
  const status = getEnvironmentPolicyStatus(env, policyType);

  return getStatusDisplayLabel(status.Status);
}

export function getEnvironmentLink(env: EnvironmentInfo) {
  const platform = getPlatformType(env.Type);
  const isAsync = isEdgeAsync(env);

  if (isAsync) {
    return {
      to: 'edge.browse.dashboard',
      params: { endpointId: env.Id },
    };
  }

  if (platform === PlatformType.Kubernetes) {
    return {
      to: 'kubernetes.dashboard',
      params: { endpointId: env.Id },
    };
  }

  return {
    to: 'docker.dashboard',
    params: { endpointId: env.Id },
  };
}
